import { Injectable, inject } from '@angular/core';
import {
  ApiDependencyProviderService,
  CollectionRequest,
  CollectionResponse,
  HttpParams,
} from '@web/common';
import { map, Observable } from 'rxjs';
import { AdminProductCollectionItemResponse } from '../responses/admin-product-collection-item.response';
import { blank, Guid } from '@web/utils';
import { AdminProductCreateRequest } from '../requests/admin-product-create.request';
import { AdminProductDetailsResponse } from '../responses/admin-product-details.response';
import { AdminProductUpdateRequest } from '../requests/admin-product-update.request';

@Injectable({
  providedIn: 'root',
})
export class AdminProductApiService {
  private readonly provider = inject(ApiDependencyProviderService);

  public fetchCollection(
    request: CollectionRequest,
  ): Observable<CollectionResponse<AdminProductCollectionItemResponse>> {
    const path = 'admin/product';
    const url = this.provider.httpUrlFactory.withQueryParams(
      this.hostUrl(),
      path,
      this.provider.httpHelper.collectionRequestToQueryParams(request),
    );
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            AdminProductCollectionItemResponse,
          ),
        ),
      );
  }

  /* prettier-ignore */
  public removeById(id: Guid): Observable<void> {
    const path = `admin/product/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, { [HttpParams.Id]: id });

    return this.provider.http.delete(url).pipe(blank())
  }

  public create(request: AdminProductCreateRequest): Observable<void> {
    const path = `admin/product`;
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();
    const body = this.provider.httpHelper.requestToBody(
      request,
      AdminProductCreateRequest,
    );

    return this.provider.http.post(url, body, { context }).pipe(blank());
  }

  /* prettier-ignore */
  public fetchDetailsById(id: Guid): Observable<AdminProductDetailsResponse> {
    const path = `admin/product/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, { [HttpParams.Id]: id });
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(AdminProductDetailsResponse),
        ),
      );
  }

  /* prettier-ignore */
  public update(
    id: Guid,
    request: AdminProductUpdateRequest,
  ): Observable<void> {
    const path = `admin/product/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, { [HttpParams.Id]: id });
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();
    const body = this.provider.httpHelper.requestToBody(
      request,
      AdminProductUpdateRequest,
    );

    return this.provider.http.put(url, body, { context }).pipe(blank());
  }

  private hostUrl(): string {
    return this.provider.environment.ApiHostUrl.Shop;
  }
}
